import type { ExpressionSpecification, Map as MapboxMap } from "mapbox-gl";
import type { AddressesResponse, AddressFeatureProperties, GeoJsonFeature } from "../../api/map/types";
import { isAllCampaignsVisibleAddress } from "../../api/map/campaignScope";
import type { FlyerType } from "../components";
import { applyIncrementalUpdate, setCachedFeatures, getCachedFeatures } from "./utils";

// Telemetry callback type - optional to avoid circular dependencies
type TelemetryCallback = (eventName: string, payload?: Record<string, unknown>) => void;
let telemetryCallback: TelemetryCallback | null = null;

/**
 * Sets the telemetry callback for render timing
 */
export const setAddressesLayerTelemetry = (callback: TelemetryCallback | null): void => {
    telemetryCallback = callback;
    // Use callback variable to avoid unused variable warning
    void telemetryCallback;
};

export interface AddressesLayerOptions {
    map: MapboxMap;
    sourceId?: string;
    selectedFlyerTypeId?: string | null;
    selectedAddressIds?: string[];
}

const SOURCE_ID = "addresses";

/** Hidden addresses are invisible below this zoom (they clutter mid zooms) and shown faintly above it. */
export const HIDDEN_VISIBLE_FROM_ZOOM = 15.5;

// ["zoom"] is only allowed as input to a top-level step/interpolate in paint properties,
// so the status match is duplicated per zoom branch instead of nesting step inside match.
const CIRCLE_OPACITY_EXPRESSION: ExpressionSpecification = [
    "step",
    ["zoom"],
    [
        "match",
        ["coalesce", ["feature-state", "overrideStatus"], ["get", "status"]],
        "hidden", 0,
        1
    ],
    HIDDEN_VISIBLE_FROM_ZOOM,
    [
        "match",
        ["coalesce", ["feature-state", "overrideStatus"], ["get", "status"]],
        "hidden", 0.3,
        1
    ]
];

const CIRCLE_STROKE_OPACITY_EXPRESSION: ExpressionSpecification = [
    "step",
    ["zoom"],
    [
        "match",
        ["coalesce", ["feature-state", "overrideStatus"], ["get", "status"]],
        "hidden", 0,
        1
    ],
    HIDDEN_VISIBLE_FROM_ZOOM,
    1
];

/** Circle colour per address status (matches iOS). Purple is the selection colour. */
export const ADDRESS_STATUS_COLORS = {
    delivered: "#114838", // dark green
    partial: "#F59E1A", // orange/amber
    pending: "#F6C85F", // yellow/amber
    blocked: "#D92626", // red
    hidden: "#858585", // gray
    selected: "#8b5cf6" // purple
} as const;

/**
 * Single source of truth for the circle colour. Both ensureAddressesLayer and
 * setAddressDeliveryOverride set it — a second, stale copy inside
 * setAddressDeliveryOverride used to repaint the whole map in an older
 * blue/green palette the first time anyone undid a delivery.
 * Feature-state "overrideStatus" wins so optimistic updates land next frame.
 */
const CIRCLE_COLOR_EXPRESSION: ExpressionSpecification = [
    "case",
    ["get", "isSelected"],
    ADDRESS_STATUS_COLORS.selected,
    [
        "match",
        ["coalesce", ["feature-state", "overrideStatus"], ["get", "status"]],
        "delivered", ADDRESS_STATUS_COLORS.delivered,
        "partial", ADDRESS_STATUS_COLORS.partial,
        "pending", ADDRESS_STATUS_COLORS.pending,
        "blocked", ADDRESS_STATUS_COLORS.blocked,
        "hidden", ADDRESS_STATUS_COLORS.hidden,
        ADDRESS_STATUS_COLORS.pending // default: pending colour
    ]
];

/**
 * Computes address status based on flyer statuses
 * Returns: "delivered" | "partial" | "pending" | "blocked" | "hidden"
 */
export const isFlyerStatusComplete = (status: { deliveredQuantity: number; isNoThanks?: boolean }): boolean =>
    status.deliveredQuantity > 0 || !!status.isNoThanks;

export const getAddressStatus = (
    properties: AddressFeatureProperties,
    selectedFlyerTypeId?: string | null,
    campaignFlyerTypes?: FlyerType[],
    allCampaignsMode?: boolean
): string => {
    if (properties.isBlocked) {
        return "blocked";
    }
    if (properties.isHidden) {
        return "hidden";
    }

    if (!properties.flyerStatuses || properties.flyerStatuses.length === 0) {
        return "pending";
    }

    if (allCampaignsMode) {
        return properties.flyerStatuses.some((fs) => isFlyerStatusComplete(fs))
            ? "delivered"
            : "pending";
    }

    // If a specific flyer type is selected, check only that one
    // selectedFlyerTypeId can be either FlyerType.id or CampaignFlyer.id
    if (selectedFlyerTypeId) {
        const flyerStatus = properties.flyerStatuses.find(
            (fs) => fs.flyerTypeId === selectedFlyerTypeId || fs.campaignFlyerId === selectedFlyerTypeId
        );
        if (!flyerStatus) {
            return "pending";
        }
        return isFlyerStatusComplete(flyerStatus) ? "delivered" : "pending";
    }

    // If campaign flyer types are provided, filter to only those
    // This ensures we only check flyer types that belong to the current campaign
    if (campaignFlyerTypes && campaignFlyerTypes.length > 0) {
        const campaignFlyerTypeIds = new Set(campaignFlyerTypes.map(ft => ft.id));

        // Filter to only statuses for flyer types in this campaign
        // Match by either flyerTypeId or campaignFlyerId
        // For campaign-specific flyers: campaignFlyerId should match CampaignFlyer.id (which is in campaignFlyerTypeIds)
        const relevantStatuses = properties.flyerStatuses.filter(
            fs => {
                // Match by FlyerTypeId if it exists
                if (fs.flyerTypeId && campaignFlyerTypeIds.has(fs.flyerTypeId)) {
                    return true;
                }
                // Match by CampaignFlyerId if it exists (for campaign-specific flyers)
                if (fs.campaignFlyerId && campaignFlyerTypeIds.has(fs.campaignFlyerId)) {
                    return true;
                }
                return false;
            }
        );

        if (relevantStatuses.length === 0) {
            return "pending";
        }

        const deliveredCount = relevantStatuses.filter((fs) => isFlyerStatusComplete(fs)).length;
        const totalCount = relevantStatuses.length;

        if (deliveredCount === totalCount) {
            return "delivered";
        }
        if (deliveredCount > 0) {
            return "partial";
        }
        return "pending";
    }

    // Fallback: Multi-flyer logic (if campaignFlyerTypes not provided)
    const deliveredCount = properties.flyerStatuses.filter(
        (fs) => isFlyerStatusComplete(fs)
    ).length;
    const totalCount = properties.flyerStatuses.length;

    if (deliveredCount === totalCount && totalCount > 0) {
        return "delivered";
    }
    if (deliveredCount > 0) {
        return "partial";
    }
    return "pending";
};

/**
 * Delivery overview: an address earns its dot only if something was actually delivered there
 * under the current flyer filter. Cached features from before the view was switched on have
 * to be dropped by the same rule, or the map keeps showing the undelivered ones.
 */
export const isDeliveredOnlyVisibleAddress = (
    properties: AddressFeatureProperties,
    selectedFlyerTypeId?: string | null,
    campaignFlyerTypes?: FlyerType[]
): boolean => {
    if (properties.isBlocked || properties.isHidden) {
        return false;
    }
    return getAddressStatus(properties, selectedFlyerTypeId, campaignFlyerTypes) !== "pending";
};

/**
 * Ensures the addresses layer exists on the map (idempotent)
 */
export const ensureAddressesLayer = ({
    map,
    sourceId = SOURCE_ID
}: AddressesLayerOptions): void => {
    // Try to create source - if style is not ready, this will fail silently
    // The updateAddressesLayer function handles waiting for style.load
    try {
        if (!map.getSource(sourceId)) {
            map.addSource(sourceId, {
                type: "geojson",
                data: { type: "FeatureCollection", features: [] }
            });
        }
    } catch (error) {
        // If source creation fails (style not loaded), just return
        // The caller (updateAddressesLayer) will handle waiting for style.load
        return;
    }

    // Remove existing layer if it exists (to update styling)
    const layerId = `${sourceId}-points`;
    if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
    }
    try {
    map.addLayer({
        id: layerId,
        type: "circle",
        source: sourceId,
        layout: {
            visibility: "visible" // Explicitly set visibility
        },
        paint: {
            // Base radius from zoom, scaled up for high deliveryUnitCount (many lador per point).
            // scale = clamp(1..2.3, 1 + 0.12 * (sqrt(max(1, n)) - 1)); ~2.1x at n=100.
            "circle-radius": [
                // zoom must be the direct input to the top-level interpolate
                // deliveryUnitCount scaling is applied per-stop as a feature expression
                "interpolate",
                ["linear"],
                ["zoom"],
                10, ["*", 4,  ["max", 1, ["min", 2.3, ["+", 1, ["*", 0.12, ["-", ["sqrt", ["max", 1, ["coalesce", ["get", "deliveryUnitCount"], 1]]], 1]]]]]],
                12, ["*", 6,  ["max", 1, ["min", 2.3, ["+", 1, ["*", 0.12, ["-", ["sqrt", ["max", 1, ["coalesce", ["get", "deliveryUnitCount"], 1]]], 1]]]]]],
                15, ["*", 9,  ["max", 1, ["min", 2.3, ["+", 1, ["*", 0.12, ["-", ["sqrt", ["max", 1, ["coalesce", ["get", "deliveryUnitCount"], 1]]], 1]]]]]],
                18, ["*", 12, ["max", 1, ["min", 2.3, ["+", 1, ["*", 0.12, ["-", ["sqrt", ["max", 1, ["coalesce", ["get", "deliveryUnitCount"], 1]]], 1]]]]]]
            ],
            "circle-color": CIRCLE_COLOR_EXPRESSION,
            "circle-stroke-color": [
                "case",
                ["get", "isSelected"],
                "#ffffff", // white stroke for selected
                "#ffffff"
            ],
            "circle-stroke-width": [
                "case",
                ["get", "isSelected"],
                3, // thicker stroke for selected
                1
            ],
            "circle-opacity": CIRCLE_OPACITY_EXPRESSION,
            "circle-stroke-opacity": CIRCLE_STROKE_OPACITY_EXPRESSION
        }
    });
    } catch (e) {
        console.error("[AddressesLayer] addLayer failed — paint expression invalid:", e);
    }
};

// Track maps where the paint expression has been updated to support feature-state overrides
const paintExpressionUpdated = new WeakSet<MapboxMap>();

/**
 * Sets an immediate delivery status override on a single address using Mapbox feature-state.
 * This is independent of the data pipeline (setData/React Query), so it takes effect on the
 * next frame and persists through subsequent updateAddressesLayer calls.
 * Use null to clear the override.
 */
export const setAddressDeliveryOverride = (
    map: MapboxMap,
    addressId: string,
    status: "delivered" | "pending" | null
): void => {
    const layerId = `${SOURCE_ID}-points`;

    // Ensure the paint expression uses ["feature-state", "overrideStatus"] — do this only once per map.
    // New layers created by ensureAddressesLayer already include it; this updates any pre-existing layer.
    if (!paintExpressionUpdated.has(map) && map.getLayer(layerId)) {
        try {
            map.setPaintProperty(layerId, "circle-color", CIRCLE_COLOR_EXPRESSION);
            map.setPaintProperty(layerId, "circle-opacity", CIRCLE_OPACITY_EXPRESSION);
            map.setPaintProperty(layerId, "circle-stroke-opacity", CIRCLE_STROKE_OPACITY_EXPRESSION);
            paintExpressionUpdated.add(map);
        } catch (e) {
            console.warn("[AddressesLayer] Could not update paint property for feature-state override:", e);
        }
    }

    if (status === null) {
        try {
            map.removeFeatureState({ source: SOURCE_ID, id: addressId }, "overrideStatus");
        } catch (e) {
            // Source may not exist yet
        }
    } else {
        try {
            map.setFeatureState({ source: SOURCE_ID, id: addressId }, { overrideStatus: status });
        } catch (e) {
            console.warn("[AddressesLayer] Could not set feature-state override:", e);
        }
    }
};

// Track if we're waiting for style.load to avoid registering multiple listeners
let pendingStyleLoad = false;

/**
 * Updates the addresses layer with data from the API
 * Uses incremental updates when possible to minimize re-rendering
 */
export const updateAddressesLayer = (
    map: MapboxMap,
    data: AddressesResponse | null,
    selectedFlyerTypeId?: string | null,
    useIncremental: boolean = true,
    isDrawingPolygon: boolean = false,
    selectedAddressIds?: string[],
    campaignFlyerTypes?: FlyerType[],
    allCampaignsMode?: boolean,
    allCampaignsShowUndelivered?: boolean,
    deliveredOnlyMode?: boolean
): void => {
    // Check if source and layer exist - if they do, we can update even if isStyleLoaded() returns false
    // (isStyleLoaded() can return false even after style.load event has fired)
    let source = map.getSource(SOURCE_ID);
    const layerId = `${SOURCE_ID}-points`;
    let layer = map.getLayer(layerId);

    // If source and layer don't exist AND style is not loaded, wait for style.load
    if ((!source || !layer) && !map.isStyleLoaded()) {
        // Only register ONE style.load listener if we haven't already
        if (!pendingStyleLoad) {
            pendingStyleLoad = true;
            ensureAddressesLayer({ map, sourceId: SOURCE_ID, selectedFlyerTypeId });
            map.once("style.load", () => {
                pendingStyleLoad = false;
                updateAddressesLayer(map, data, selectedFlyerTypeId, useIncremental, isDrawingPolygon);
            });
        }
        return;
    }

    // Reset pendingStyleLoad flag if source and layer exist (style is actually ready)
    if (source && layer && pendingStyleLoad) {
        pendingStyleLoad = false;
    }

    if (!source || source.type !== "geojson" || !layer) {
        console.warn(`[AddressesLayer] Source or layer missing. Source: ${source ? 'exists' : 'missing'}, Layer: ${layer ? 'exists' : 'missing'}. Creating...`);
        ensureAddressesLayer({ map, sourceId: SOURCE_ID, selectedFlyerTypeId });

        // Get source and layer again after ensuring they exist
        source = map.getSource(SOURCE_ID);
        layer = map.getLayer(layerId);

        if (!source || source.type !== "geojson") {
            console.error(`[AddressesLayer] Failed to create source`);
            return;
        }

        if (!layer) {
            console.error(`[AddressesLayer] Failed to create layer`);
            return;
        }
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;

    // Don't clear data if it's null/undefined - keep existing features visible
    // This prevents addresses from disappearing during query refetch
    if (!data) {
        return;
    }

    if (data.features.length === 0) {
        // Only clear if we explicitly get empty data (not just null/undefined)
        geojsonSource.setData({ type: "FeatureCollection", features: [] } as any);
        return;
    }

    // All-campaigns overview: delivered only by default; optional undelivered points
    const sourceFeatures = allCampaignsMode
        ? data.features.filter((f) => isAllCampaignsVisibleAddress(f.properties, allCampaignsShowUndelivered ?? false))
        : data.features;

    // Check if features have geometry
    const featuresWithGeometry = sourceFeatures.filter(f => f.geometry && f.geometry.type === "Point");

    if (featuresWithGeometry.length === 0) {
        console.warn(`[AddressesLayer] No features with Point geometry!`);
        return;
    }

    // Convert API response to Mapbox GeoJSON format with computed status
    const newFeatures: GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>[] = sourceFeatures.map((feature) => {
        const addressId = feature.id ?? feature.properties.addressPointId;
        const isSelected = selectedAddressIds ? selectedAddressIds.includes(String(addressId)) : false;

        return {
            type: "Feature" as const,
            id: addressId,
            geometry: feature.geometry,
            properties: {
                ...feature.properties,
                status: getAddressStatus(feature.properties, selectedFlyerTypeId, campaignFlyerTypes, allCampaignsMode),
                // Add helper properties for filtering/styling
                addressPointId: feature.properties.addressPointId,
                deliveredCount: feature.properties.flyerStatuses.filter(
                    (fs) => isFlyerStatusComplete(fs)
                ).length,
                totalFlyerCount: feature.properties.flyerStatuses.length,
                isSelected
            }
        };
    });

    // Use incremental update if enabled (but not when flyer filter changes, as status changes)
    // For addresses, use keepExistingFeatures=true to prevent addresses from disappearing when zooming out
    // BUT: When we have merged data from multiple tiles, always do full update to ensure all tiles are included
    // DISABLED: Incremental updates can miss features when viewport changes during panning
    // Always use full update for addresses to ensure all features are visible when panning
    const isMergedData = data.features.length > 500; // Heuristic: merged data usually has more features
    if (false && useIncremental && selectedFlyerTypeId === undefined && !isMergedData) {
        try {
            applyIncrementalUpdate(
                map,
                geojsonSource,
                SOURCE_ID,
                newFeatures,
                (feature) => feature.id ?? feature.properties.addressPointId,
                true // keepExistingFeatures: keep all addresses, only add new ones
            );

            // Update layer styling if needed
            if (map.getLayer(`${SOURCE_ID}-points`)) {
                ensureAddressesLayer({
                    map,
                    sourceId: SOURCE_ID,
                    selectedFlyerTypeId,
                    selectedAddressIds
                });
            }
            return;
        } catch (error) {
            // Fallback to full update on error
            console.warn("Incremental update failed, falling back to full update:", error);
        }
    }

    // Full update (incremental disabled, filter changed, or error)
    // For addresses, merge with existing features to prevent disappearing when zooming out
    const currentFeatures = getCachedFeatures<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>(map, SOURCE_ID);
    const mergedFeatures = new Map<string | number, GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>>();

    // Add all existing features, updating isSelected if needed
    currentFeatures.forEach((feature) => {
        const id = feature.id ?? feature.properties.addressPointId;
        if (id !== undefined) {
            if (
                allCampaignsMode &&
                !isAllCampaignsVisibleAddress(feature.properties, allCampaignsShowUndelivered ?? false)
            ) {
                return;
            }
            if (
                deliveredOnlyMode &&
                !isDeliveredOnlyVisibleAddress(feature.properties, selectedFlyerTypeId, campaignFlyerTypes)
            ) {
                return;
            }
            const isSelected = selectedAddressIds ? selectedAddressIds.includes(String(id)) : false;
            mergedFeatures.set(id, {
                ...feature,
                properties: {
                    ...feature.properties,
                    // Recompute rather than carry the baked status over. These are features the
                    // current response did not re-send (outside the fetched viewport, or dropped
                    // by the server's feature cap), and the paint expression colours from
                    // ["get", "status"]. Keeping the old value froze them at whatever the status
                    // was under the previous flyer filter — a point delivered later, or viewed
                    // under a different Flygblad, stayed yellow/partial indefinitely.
                    status: getAddressStatus(
                        feature.properties,
                        selectedFlyerTypeId,
                        campaignFlyerTypes,
                        allCampaignsMode
                    ),
                    isSelected
                }
            });
        }
    });

    // Add/update with new features (will overwrite existing if same ID)
    // Trust flyerStatuses from the incoming combined addresses response (static + deliveries merge in hooks).
    // Previously we merged with cached statuses when new had fewer/empty rows; that kept addresses green after
    // "ångra utdelning" when refetch correctly returned no deliveries for that address.
    newFeatures.forEach((feature) => {
        const id = feature.id ?? feature.properties.addressPointId;
        if (id !== undefined) {
            mergedFeatures.set(id, feature);
        }
    });

    // Update isSelected for existing features that weren't in newFeatures
    mergedFeatures.forEach((feature, id) => {
        if (!newFeatures.find(f => (f.id ?? f.properties.addressPointId) === id)) {
            const isSelected = selectedAddressIds ? selectedAddressIds.includes(String(id)) : false;
            mergedFeatures.set(id, {
                ...feature,
                properties: {
                    ...feature.properties,
                    isSelected
                }
            });
        }
    });

    const mergedArray = Array.from(mergedFeatures.values()).filter((feature) => {
        if (!allCampaignsMode) {
            return true;
        }
        return isAllCampaignsVisibleAddress(feature.properties, allCampaignsShowUndelivered ?? false);
    });
    const geojson = {
        type: "FeatureCollection" as const,
        features: mergedArray
    };

    // Set data and cache features
    geojsonSource.setData(geojson as any);
    setCachedFeatures(map, SOURCE_ID, mergedArray);

    // Ensure layer exists and is styled correctly
    if (!map.getLayer(layerId)) {
        console.warn(`[AddressesLayer] Layer ${layerId} missing after setData, recreating...`);
        ensureAddressesLayer({
            map,
            sourceId: SOURCE_ID,
            selectedFlyerTypeId
        });
    } else {
        // Set layer visibility based on polygon drawing state
        const targetVisibility = isDrawingPolygon ? "none" : "visible";
        const layerVisibility = map.getLayoutProperty(layerId, "visibility");
        if (layerVisibility !== targetVisibility) {
            map.setLayoutProperty(layerId, "visibility", targetVisibility);
        }
    }

    // setData() schedules Mapbox's own render pipeline — no manual repaint needed.
};

/**
 * Re-applies all-campaigns visibility on cached map features (e.g. when toggling undelivered filter).
 */
export const applyAllCampaignsAddressVisibilityFilter = (
    map: MapboxMap,
    showUndelivered: boolean
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;
    const currentFeatures = getCachedFeatures<
        AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }
    >(map, SOURCE_ID);

    if (currentFeatures.length === 0) {
        return;
    }

    const filtered = currentFeatures.filter((feature) =>
        isAllCampaignsVisibleAddress(feature.properties, showUndelivered)
    );

    geojsonSource.setData({
        type: "FeatureCollection",
        features: filtered
    } as any);
    setCachedFeatures(map, SOURCE_ID, filtered);
};

/**
 * Prunes the already-drawn points down to the delivered ones the moment the delivery
 * overview is switched on, instead of waiting for the next fetch to land.
 */
export const applyDeliveredOnlyAddressVisibilityFilter = (
    map: MapboxMap,
    selectedFlyerTypeId?: string | null,
    campaignFlyerTypes?: FlyerType[]
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;
    const currentFeatures = getCachedFeatures<
        AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }
    >(map, SOURCE_ID);

    if (currentFeatures.length === 0) {
        return;
    }

    const filtered = currentFeatures.filter((feature) =>
        isDeliveredOnlyVisibleAddress(feature.properties, selectedFlyerTypeId, campaignFlyerTypes)
    );

    geojsonSource.setData({
        type: "FeatureCollection",
        features: filtered
    } as any);
    setCachedFeatures(map, SOURCE_ID, filtered);
};

/**
 * Updates a single address point's position on the map immediately
 * This provides instant visual feedback before the API response
 */
export const updateAddressPosition = (
    map: MapboxMap,
    addressId: string | number,
    newLat: number,
    newLon: number
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;

    // Get current data from cache first, fallback to source data
    let currentFeatures = getCachedFeatures<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>(map, SOURCE_ID);

    // If cache is empty, try to get from source directly
    if (currentFeatures.length === 0) {
        try {
            const sourceData = (geojsonSource as any)._data as { type: "FeatureCollection"; features: Array<GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>> } | null;
            if (sourceData && sourceData.features) {
                currentFeatures = sourceData.features;
            }
        } catch (error) {
            console.warn("[AddressesLayer] Could not access source data for position update:", error);
            return;
        }
    }

    const featureToUpdate = currentFeatures.find(
        (f) => {
            const id = f.id ?? f.properties.addressPointId;
            return id === addressId || String(id) === String(addressId);
        }
    );

    if (!featureToUpdate) {
        console.warn(`[AddressesLayer] Address ${addressId} not found for position update`);
        return;
    }

    const updatedFeature: GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }> = {
        ...featureToUpdate,
        geometry: {
            type: "Point",
            coordinates: [newLon, newLat]
        }
    };

    const updatedFeatures = currentFeatures.map((f) => {
        const id = f.id ?? f.properties.addressPointId;
        if (id === addressId || String(id) === String(addressId)) {
            return updatedFeature;
        }
        return f;
    });

    geojsonSource.setData({
        type: "FeatureCollection",
        features: updatedFeatures
    } as any);

    setCachedFeatures(map, SOURCE_ID, updatedFeatures);
};

/**
 * Optimistically applies a merge: moves primary to new location, updates unit count, hides others.
 */
export const applyAddressMergeOnMap = (
    map: MapboxMap,
    primaryAddressId: string,
    latitude: number,
    longitude: number,
    deliveryUnitCount: number,
    hiddenAddressIds: string[],
    selectedFlyerTypeId?: string | null
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;
    let currentFeatures = getCachedFeatures<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>(map, SOURCE_ID);

    if (currentFeatures.length === 0) {
        try {
            const sourceData = geojsonSource._data as { type: "FeatureCollection"; features: typeof currentFeatures } | null;
            if (sourceData?.features) {
                currentFeatures = sourceData.features;
            }
        } catch {
            return;
        }
    }

    const hiddenSet = new Set(hiddenAddressIds.map(String));
    const updatedFeatures = currentFeatures.map((f) => {
        const id = String(f.id ?? f.properties.addressPointId);
        if (id === String(primaryAddressId)) {
            const updatedProperties = {
                ...f.properties,
                deliveryUnitCount
            };
            return {
                ...f,
                geometry: {
                    type: "Point" as const,
                    coordinates: [longitude, latitude] as [number, number]
                },
                properties: {
                    ...updatedProperties,
                    status: getAddressStatus(updatedProperties, selectedFlyerTypeId)
                }
            };
        }
        if (hiddenSet.has(id)) {
            const updatedProperties = {
                ...f.properties,
                isHidden: true
            };
            return {
                ...f,
                properties: {
                    ...updatedProperties,
                    status: getAddressStatus(updatedProperties, selectedFlyerTypeId)
                }
            };
        }
        return f;
    });

    geojsonSource.setData({
        type: "FeatureCollection",
        features: updatedFeatures
    } as any);

    setCachedFeatures(map, SOURCE_ID, updatedFeatures);
};

/**
 * Updates a single address point's status (blocked/hidden) on the map immediately
 * This provides instant visual feedback before the API response
 */
export const updateAddressStatus = (
    map: MapboxMap,
    addressId: string | number,
    isBlocked?: boolean,
    isHidden?: boolean,
    selectedFlyerTypeId?: string | null
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;

    // Get current data from cache first, fallback to source data
    let currentFeatures = getCachedFeatures<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>(map, SOURCE_ID);

    // If cache is empty, try to get from source directly
    if (currentFeatures.length === 0) {
        try {
            const sourceData = geojsonSource._data as { type: "FeatureCollection"; features: Array<GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>> } | null;
            if (sourceData && sourceData.features) {
                currentFeatures = sourceData.features;
            }
        } catch (error) {
            // If we can't access source data, just return
            console.warn("[AddressesLayer] Could not access source data for status update:", error);
            return;
        }
    }

    // Find the feature to update
    const featureToUpdate = currentFeatures.find(
        (f) => {
            const id = f.id ?? f.properties.addressPointId;
            return id === addressId || String(id) === String(addressId);
        }
    );

    if (!featureToUpdate) {
        // Feature not found - might not be loaded yet
        console.warn(`[AddressesLayer] Address ${addressId} not found in current features for status update`);
        return;
    }

    // Update the feature's properties
    const updatedProperties = {
        ...featureToUpdate.properties,
        isBlocked: isBlocked !== undefined ? isBlocked : featureToUpdate.properties.isBlocked,
        isHidden: isHidden !== undefined ? isHidden : featureToUpdate.properties.isHidden
    };

    // Recalculate status based on updated properties
    const updatedStatus = getAddressStatus(updatedProperties, selectedFlyerTypeId);

    const updatedFeature: GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }> = {
        ...featureToUpdate,
        properties: {
            ...updatedProperties,
            status: updatedStatus
        }
    };

    // Update all features: replace the updated one, keep others
    const updatedFeatures = currentFeatures.map((f) => {
        const id = f.id ?? f.properties.addressPointId;
        if (id === addressId || String(id) === String(addressId)) {
            return updatedFeature;
        }
        return f;
    });

    // Update the source
    geojsonSource.setData({
        type: "FeatureCollection",
        features: updatedFeatures
    } as any);

    // Update cache
    setCachedFeatures(map, SOURCE_ID, updatedFeatures);
};

/**
 * Optimistically marks addresses as delivered immediately after delivery is created.
 * Updates the flyerStatuses to include the new deliveries and recalculates status.
 * 
 * @param map - The Mapbox map instance
 * @param addressIds - Array of address IDs to mark as delivered
 * @param flyerTypeIds - Array of flyer type IDs that were delivered
 * @param selectedFlyerTypeId - Optional selected flyer type ID for status calculation
 * @param campaignFlyerTypes - Optional campaign flyer types for status calculation
 */
export const markAddressesAsDelivered = (
    map: MapboxMap,
    addressIds: string[],
    flyerTypeIds: string[],
    selectedFlyerTypeId?: string | null,
    campaignFlyerTypes?: FlyerType[],
    options?: { isNoThanks?: boolean }
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;
    const addressIdSet = new Set(addressIds.map(id => String(id)));

    // Get current data from cache first, fallback to source data
    let currentFeatures = getCachedFeatures<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>(map, SOURCE_ID);

    // If cache is empty, try to get from source directly
    if (currentFeatures.length === 0) {
        try {
            const sourceData = geojsonSource._data as { type: "FeatureCollection"; features: Array<GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>> } | null;
            if (sourceData && sourceData.features) {
                currentFeatures = sourceData.features;
            }
        } catch (error) {
            console.warn("[AddressesLayer] Could not access source data for marking as delivered:", error);
            return;
        }
    }

    // Update features that match the address IDs
    let updatedCount = 0;
    const updatedFeatures = currentFeatures.map((feature) => {
        const id = feature.id ?? feature.properties.addressPointId;
        const addressIdStr = String(id);
        
        if (!addressIdSet.has(addressIdStr)) {
            return feature; // Not in the list of addresses to update
        }

        updatedCount++;

        // Get existing flyerStatuses or create empty array
        const existingFlyerStatuses = feature.properties.flyerStatuses || [];
        const flyerStatusMap = new Map(existingFlyerStatuses.map(fs => [fs.flyerTypeId, fs]));

        // Update or add flyerStatus for each delivered flyerTypeId
        for (const flyerTypeId of flyerTypeIds) {
            const existingStatus = flyerStatusMap.get(flyerTypeId);
            if (existingStatus) {
                flyerStatusMap.set(flyerTypeId, {
                    ...existingStatus,
                    deliveredQuantity: options?.isNoThanks ? 0 : Math.max(existingStatus.deliveredQuantity, 1),
                    isNoThanks: options?.isNoThanks ?? false,
                    lastDeliveredAt: new Date().toISOString()
                });
            } else {
                flyerStatusMap.set(flyerTypeId, {
                    addressPointId: feature.properties.addressPointId,
                    campaignId: "",
                    flyerTypeId: flyerTypeId,
                    deliveredQuantity: options?.isNoThanks ? 0 : 1,
                    isNoThanks: options?.isNoThanks ?? false,
                    lastDeliveredAt: new Date().toISOString(),
                    recordedById: ""
                });
            }
        }

        // Convert map back to array
        const updatedFlyerStatuses = Array.from(flyerStatusMap.values());

        // Update properties with new flyerStatuses
        const updatedProperties = {
            ...feature.properties,
            flyerStatuses: updatedFlyerStatuses
        };

        // Recalculate status based on updated flyerStatuses
        const updatedStatus = getAddressStatus(updatedProperties, selectedFlyerTypeId, campaignFlyerTypes);

        // Calculate deliveredCount and totalFlyerCount
        const deliveredCount = updatedFlyerStatuses.filter((fs) => isFlyerStatusComplete(fs)).length;
        const totalFlyerCount = updatedFlyerStatuses.length;

        return {
            ...feature,
            properties: {
                ...updatedProperties,
                status: updatedStatus,
                deliveredCount,
                totalFlyerCount
            }
        };
    });

    if (updatedCount === 0) {
        // No addresses found - might not be loaded yet
        console.warn(`[AddressesLayer] No addresses found to mark as delivered (IDs: ${addressIds.join(", ")})`);
        return;
    }

    // Update the source
    geojsonSource.setData({
        type: "FeatureCollection",
        features: updatedFeatures
    } as any);

    // Update cache
    setCachedFeatures(map, SOURCE_ID, updatedFeatures);
};

/**
 * Optimistically marks addresses as undelivered (pending) for the given flyer types.
 * Removes those deliveries from flyerStatuses and recalculates status. The whole batch
 * goes through one setData call — looping the single-address form would rewrite the
 * entire feature collection once per address.
 *
 * @param map - The Mapbox map instance
 * @param addressIds - The address IDs to mark as undelivered
 * @param flyerTypeIds - The flyer type IDs to remove deliveries for
 * @param selectedFlyerTypeId - Optional selected flyer type ID for status calculation
 * @param campaignFlyerTypes - Optional campaign flyer types for status calculation
 */
export const markAddressesAsUndelivered = (
    map: MapboxMap,
    addressIds: Array<string | number>,
    flyerTypeIds: string[],
    selectedFlyerTypeId?: string | null,
    campaignFlyerTypes?: FlyerType[]
): void => {
    if (addressIds.length === 0 || flyerTypeIds.length === 0) {
        return;
    }

    const addressIdSet = new Set(addressIds.map((id) => String(id)));
    const flyerTypeIdSet = new Set(flyerTypeIds);

    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        console.warn("[AddressesLayer] markAddressesAsUndelivered: Source not found");
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;

    // Get current data from cache first, fallback to source data
    let currentFeatures = getCachedFeatures<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>(map, SOURCE_ID);

    // If cache is empty, try to get from source directly
    if (currentFeatures.length === 0) {
        try {
            const sourceData = (geojsonSource as any)._data as { type: "FeatureCollection"; features: Array<GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>> } | null;
            if (sourceData && sourceData.features) {
                currentFeatures = sourceData.features;
            }
        } catch (error) {
            console.warn("[AddressesLayer] Could not access source data for marking as undelivered:", error);
            return;
        }
    }

    // Find and update the features
    let foundCount = 0;
    const updatedFeatures = currentFeatures.map((feature) => {
        const id = feature.id ?? feature.properties.addressPointId;

        if (!addressIdSet.has(String(id))) {
            return feature; // Not one of the addresses we're looking for
        }

        foundCount++;

        // Remove flyerStatus for the specified flyer types
        const existingFlyerStatuses = feature.properties.flyerStatuses || [];
        const updatedFlyerStatuses = existingFlyerStatuses.filter(
            (fs) =>
                !(fs.flyerTypeId && flyerTypeIdSet.has(fs.flyerTypeId)) &&
                !(fs.campaignFlyerId && flyerTypeIdSet.has(fs.campaignFlyerId))
        );

        // Update properties with filtered flyerStatuses
        const updatedProperties = {
            ...feature.properties,
            flyerStatuses: updatedFlyerStatuses
        };

        // Recalculate status based on updated flyerStatuses
        const updatedStatus = getAddressStatus(updatedProperties, selectedFlyerTypeId, campaignFlyerTypes);

        // Calculate deliveredCount and totalFlyerCount
        const deliveredCount = updatedFlyerStatuses.filter((fs) => isFlyerStatusComplete(fs)).length;
        const totalFlyerCount = updatedFlyerStatuses.length;

        return {
            ...feature,
            properties: {
                ...updatedProperties,
                status: updatedStatus,
                deliveredCount,
                totalFlyerCount
            }
        };
    });

    if (foundCount === 0) {
        console.warn(`[AddressesLayer] No addresses found for marking as undelivered (IDs: ${addressIds.join(", ")})`);
        return;
    }

    // Update the source
    geojsonSource.setData({
        type: "FeatureCollection",
        features: updatedFeatures
    } as any);

    // Update cache
    setCachedFeatures(map, SOURCE_ID, updatedFeatures);
};

/** Single-address form of {@link markAddressesAsUndelivered}. */
export const markAddressAsUndelivered = (
    map: MapboxMap,
    addressId: string | number,
    flyerTypeId: string,
    selectedFlyerTypeId?: string | null,
    campaignFlyerTypes?: FlyerType[]
): void => {
    markAddressesAsUndelivered(map, [addressId], [flyerTypeId], selectedFlyerTypeId, campaignFlyerTypes);
};